/**
 * useStartInSlack: the "Start in Slack" path of the compose modal (Slack
 * Twins plan E6).
 *
 * Shown only when the server has the Slack app and the org has a workspace
 * (the D1 install status). Choosing "In Slack" loads the channels Twins and
 * the user share (`GET /slack-twins/channels`); an unlinked user gets where to
 * connect instead. Start posts once per intent: the `clientRequestId` stays
 * the same until the twins, the audience or the target change, so a double
 * click or a retry gives one room. While the server answers "starting" (an
 * earlier Start with the same id is still creating the room) it asks again
 * with the same id. The id sits in sessionStorage (per intent) from the first
 * press until `started`, so a reload mid-start resumes the same room.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'

import { slackTwinsApi, SlackTwinsChannel } from '@/lib/api'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { detectTrialLimitError, TrialLimitKind } from '@/lib/billing/trial-limits'
import i18n from '@/i18n'

export type StartInSlackWhere = 'app' | 'slack'
export type StartInSlackTarget = 'dm' | 'channel'

export type StartInSlackChannelsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'notLinked'; connectUrl: string }
  | { status: 'linked'; channels: Array<SlackTwinsChannel> }

export type StartInSlackError =
  | 'channelUnavailable'
  | 'stillStarting'
  | 'startError'
  /** A known refusal (too many twins, twins unavailable, twin chat off), in the web's copy. */
  | 'refused'

/** A Slack room holds at most this many twins (the E2 starter's cap). */
export const START_IN_SLACK_MAX_TWINS = 10

export interface StartInSlackStarted {
  roomToken: string
  permalink: string | null
  /** `null` for a just-me room. */
  channelName: string | null
}

export interface UseStartInSlackOptions {
  /** False for guest, audience-only and picker-only modes: never shown. */
  isEnabled: boolean
  populationToken: string | null
  participantPersonaIds: Array<string>
}

export interface UseStartInSlackResult {
  isAvailable: boolean
  where: StartInSlackWhere
  setWhere: (where: StartInSlackWhere) => void
  target: StartInSlackTarget
  setTarget: (target: StartInSlackTarget) => void
  channelId: string | null
  setChannelId: (channelId: string | null) => void
  selectedChannel: SlackTwinsChannel | null
  channels: StartInSlackChannelsState
  reloadChannels: () => void
  isStarting: boolean
  canStart: boolean
  error: StartInSlackError | null
  /** Set with `error: 'refused'`. */
  refusal: string | null
  /** More twins picked than a Slack room holds: In Slack is off. */
  hasTooManyTwins: boolean
  /** A trial or plan cap: the app's own upgrade dialog. */
  isRoomLimitOpen: boolean
  trialLimitKind: TrialLimitKind
  closeRoomLimit: () => void
  started: StartInSlackStarted | null
  start: () => Promise<void>
  /** I4: `DELETE /slack-twins/link`, then the not-linked state. */
  disconnect: () => Promise<void>
  isDisconnecting: boolean
  hasDisconnectError: boolean
}

/** How often, and how many times, to ask again while the room is "starting". */
export const START_IN_SLACK_POLL_MS = 1500
export const START_IN_SLACK_MAX_POLLS = 8

function newClientRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // RFC 4122 v4 shape from Math.random, for runtimes without randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

const STORAGE_PREFIX = 'slack-twins:start:'

/**
 * A stored id older than the server's claim lifetime (the reconcile job drops
 * an unattached claim after 10 min) is ignored: one room per intent holds
 * inside that window, and a much later Start is a new one.
 */
export const START_IN_SLACK_STORED_ID_TTL_MS = 10 * 60 * 1000

interface StoredRequestId {
  id: string
  at: number
}

/** The id of a Start not yet answered `started`, so a reload resumes it. */
function readStoredRequestId(intentKey: string): string | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + intentKey)
    if (!raw) {
      return null
    }
    const stored = JSON.parse(raw) as Partial<StoredRequestId>
    if (typeof stored.id !== 'string' || typeof stored.at !== 'number') {
      return null
    }
    if (Date.now() - stored.at > START_IN_SLACK_STORED_ID_TTL_MS) {
      window.sessionStorage.removeItem(STORAGE_PREFIX + intentKey)
      return null
    }
    return stored.id
  } catch {
    return null
  }
}

function storeRequestId(intentKey: string, clientRequestId: string): void {
  try {
    const stored: StoredRequestId = { id: clientRequestId, at: Date.now() }
    window.sessionStorage.setItem(STORAGE_PREFIX + intentKey, JSON.stringify(stored))
  } catch {
    // Storage blocked: the id lives for this mount only.
  }
}

function clearStoredRequestId(intentKey: string): void {
  try {
    window.sessionStorage.removeItem(STORAGE_PREFIX + intentKey)
  } catch {
    // Nothing stored.
  }
}

function errorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) {
    return undefined
  }
  const code = (error.response?.data as { code?: unknown } | undefined)?.code
  return typeof code === 'string' ? code : undefined
}

/**
 * The web's own words for a known refusal, by the server's stable code (the
 * E2 starter's `too_many_twins` / `twins_unavailable`). Never the server's
 * message: anything unmapped is the generic start error.
 */
function refusalMessage(error: unknown): string | null {
  switch (errorCode(error)) {
    case 'too_many_twins':
      return i18n.t('slackTwins.compose.tooManyTwins', { ns: 'app' })
    case 'twins_unavailable':
      return i18n.t('slackTwins.compose.twinsUnavailable', { ns: 'app' })
    case 'channel_shared_externally':
      return i18n.t('slackTwins.compose.channelSharedExternally', { ns: 'app' })
    default:
      return null
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function useStartInSlack(options: UseStartInSlackOptions): UseStartInSlackResult {
  const { isEnabled, populationToken, participantPersonaIds } = options

  const [isAvailable, setIsAvailable] = useState(false)
  const [where, setWhereState] = useState<StartInSlackWhere>('app')
  const [target, setTargetState] = useState<StartInSlackTarget>('dm')
  const [channelId, setChannelIdState] = useState<string | null>(null)
  const [channels, setChannels] = useState<StartInSlackChannelsState>({ status: 'idle' })
  const [isStarting, setIsStarting] = useState(false)
  const [error, setError] = useState<StartInSlackError | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [isRoomLimitOpen, setIsRoomLimitOpen] = useState(false)
  const [trialLimitKind, setTrialLimitKind] = useState<TrialLimitKind>('rooms')
  const [started, setStarted] = useState<StartInSlackStarted | null>(null)

  const hasTooManyTwins = participantPersonaIds.length > START_IN_SLACK_MAX_TWINS
  const effectiveWhere: StartInSlackWhere = hasTooManyTwins ? 'app' : where

  const clientRequestIdRef = useRef<string | null>(null)
  const isStartingRef = useRef(false)
  const isMountedRef = useRef(true)

  const intentKey = useMemo(
    () =>
      [
        populationToken ?? '',
        [...participantPersonaIds].sort().join(','),
        target,
        target === 'channel' ? channelId ?? '' : '',
      ].join('|'),
    [populationToken, participantPersonaIds, target, channelId],
  )

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // A new intent (other twins, audience or target) gets a new claim key.
  useEffect(() => {
    clientRequestIdRef.current = null
    setError(null)
  }, [intentKey])

  useEffect(() => {
    if (!isEnabled) {
      setIsAvailable(false)
      return
    }
    let isCancelled = false
    // Any failure (or a test double without the endpoint) just hides the path.
    Promise.resolve()
      .then(() => slackTwinsApi.getInstallStatus())
      .then((status) => {
        if (!isCancelled) {
          setIsAvailable(status.available && status.connected)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setIsAvailable(false)
        }
      })
    return () => {
      isCancelled = true
    }
  }, [isEnabled])

  const loadChannels = useCallback(async () => {
    setChannels({ status: 'loading' })
    try {
      const result = await slackTwinsApi.listChannels()
      if (!isMountedRef.current) {
        return
      }
      if (result.linked) {
        setChannels({ status: 'linked', channels: result.channels })
        setChannelIdState((current) =>
          current && result.channels.some((channel) => channel.channelId === current)
            ? current
            : null,
        )
      } else {
        setChannels({ status: 'notLinked', connectUrl: result.connectUrl })
      }
    } catch {
      if (isMountedRef.current) {
        setChannels({ status: 'error' })
      }
    }
  }, [])

  const reloadChannels = useCallback(() => {
    void loadChannels()
  }, [loadChannels])

  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [hasDisconnectError, setHasDisconnectError] = useState(false)

  const disconnect = useCallback(async () => {
    setIsDisconnecting(true)
    setHasDisconnectError(false)
    try {
      await slackTwinsApi.disconnectSlack()
      if (isMountedRef.current) {
        await loadChannels()
      }
    } catch {
      if (isMountedRef.current) {
        setHasDisconnectError(true)
      }
    } finally {
      if (isMountedRef.current) {
        setIsDisconnecting(false)
      }
    }
  }, [loadChannels])

  useEffect(() => {
    if (isAvailable && effectiveWhere === 'slack' && channels.status === 'idle') {
      void loadChannels()
    }
  }, [isAvailable, effectiveWhere, channels.status, loadChannels])

  // Back from connecting in Slack: check again when the tab regains focus.
  useEffect(() => {
    if (channels.status !== 'notLinked') {
      return
    }
    const onFocus = () => {
      void loadChannels()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
    }
  }, [channels.status, loadChannels])

  const selectedChannel = useMemo(() => {
    if (channels.status !== 'linked' || channelId === null) {
      return null
    }
    return channels.channels.find((channel) => channel.channelId === channelId) ?? null
  }, [channels, channelId])

  const canStart =
    isAvailable &&
    effectiveWhere === 'slack' &&
    channels.status === 'linked' &&
    populationToken !== null &&
    participantPersonaIds.length > 0 &&
    (target === 'dm' || selectedChannel !== null) &&
    !isStarting

  const setWhere = useCallback((next: StartInSlackWhere) => {
    setWhereState(next)
  }, [])

  const setTarget = useCallback((next: StartInSlackTarget) => {
    setTargetState(next)
  }, [])

  const closeRoomLimit = useCallback(() => {
    setIsRoomLimitOpen(false)
  }, [])

  const setChannelId = useCallback((next: string | null) => {
    setChannelIdState(next)
  }, [])

  const start = useCallback(async () => {
    if (!canStart || isStartingRef.current || populationToken === null) {
      return
    }
    isStartingRef.current = true
    setIsStarting(true)
    setError(null)
    setRefusal(null)
    if (clientRequestIdRef.current === null) {
      clientRequestIdRef.current = readStoredRequestId(intentKey) ?? newClientRequestId()
      storeRequestId(intentKey, clientRequestIdRef.current)
    }
    const clientRequestId = clientRequestIdRef.current
    const channel = target === 'channel' ? selectedChannel : null
    try {
      for (let attempt = 0; attempt <= START_IN_SLACK_MAX_POLLS; attempt += 1) {
        if (attempt > 0) {
          await wait(START_IN_SLACK_POLL_MS)
        }
        const result = await slackTwinsApi.startRoom({
          populationToken,
          participantPersonaIds,
          target: channel ? { kind: 'channel', channelId: channel.channelId } : { kind: 'dm' },
          clientRequestId,
        })
        if (result.status === 'started') {
          clearStoredRequestId(intentKey)
          if (isMountedRef.current) {
            setStarted({
              roomToken: result.roomToken,
              permalink: result.permalink,
              channelName: channel?.name ?? null,
            })
          }
          return
        }
      }
      if (isMountedRef.current) {
        setError('stillStarting')
      }
    } catch (caught) {
      if (!isMountedRef.current) {
        return
      }
      const code = errorCode(caught)
      if (code === 'channel_unavailable') {
        setError('channelUnavailable')
      } else if (code === 'slack_not_linked') {
        void loadChannels()
      } else if (detectTrialLimitError(caught)) {
        setTrialLimitKind(detectTrialLimitError(caught) ?? 'rooms')
        setIsRoomLimitOpen(true)
      } else {
        const words = blockedFeatureMessage(caught) ?? refusalMessage(caught)
        if (words) {
          setError('refused')
          setRefusal(words)
        } else {
          setError('startError')
        }
      }
    } finally {
      isStartingRef.current = false
      if (isMountedRef.current) {
        setIsStarting(false)
      }
    }
  }, [canStart, populationToken, participantPersonaIds, target, selectedChannel, loadChannels, intentKey])

  return {
    isAvailable,
    where: effectiveWhere,
    setWhere,
    target,
    setTarget,
    channelId,
    setChannelId,
    selectedChannel,
    channels,
    reloadChannels,
    isStarting,
    canStart,
    error,
    refusal,
    hasTooManyTwins,
    isRoomLimitOpen,
    trialLimitKind,
    closeRoomLimit,
    started,
    start,
    disconnect,
    isDisconnecting,
    hasDisconnectError,
  }
}
